import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { CreditCard, Download, Mic, QrCode, RefreshCw } from 'lucide-react';
import { functions } from '../lib/firebase';
import { downloadInvoicePdf } from '../services/invoicePdf';
import type { InvoiceData } from '../services/invoicePdf';
import { Badge, Button, Modal, Spinner, cx } from './ui';

type Cycle = 'monthly' | 'yearly';
type Status = 'comped' | 'paid' | 'trial' | 'lapsed' | 'suspended';

interface BillingData {
  ok: true;
  viewerRole: 'owner' | 'admin' | 'member';
  status: Status;
  trialEndsAt: string | null;
  paidUntil: string | null;
  paidThrough: string | null;
  subscription: { cycle: Cycle; method: string; status: string } | null;
  price: { monthlySgd: number; yearlySgd: number };
  limits: { members: number; creditsPerMonth: number; speechSecondsPerMonth: number };
  trial: { credits: number; speechSeconds: number };
  addOn: { seconds: number; priceSgd: number };
  pool: { key: string; credits: number; creditLimit: number; speechSeconds: number; speechLimit: number };
  invoices: InvoiceData[];
}

const getBilling = httpsCallable<void, BillingData>(functions, 'orgBilling');
const start = httpsCallable<{ cycle: Cycle; method: 'card' | 'paynow' }, { ok: true; url: string }>(
  functions,
  'startSubscription',
);
const cancel = httpsCallable<void, { ok: true }>(functions, 'cancelSubscription');
const addOn = httpsCallable<void, { ok: true; url: string }>(functions, 'buySpeechAddOn');

const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
const sgd = (n: number) => `S$${Number.isInteger(n) ? n : n.toFixed(2)}`;
const mins = (s: number) => (s >= 3600 ? `${+(s / 3600).toFixed(1)} h` : `${Math.round(s / 60)} min`);

/**
 * The organisation's plan: where it stands, subscribing (card renews by
 * itself; PayNow is paid each time), cancelling, the spoken-notes add-on, and
 * every invoice. Never shown in the iPhone app (App Store guideline 3.1.1).
 */
export default function OrgBilling({ onChanged }: { onChanged: () => void }) {
  const [data, setData] = useState<BillingData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const returning = useRef(/[?&](paid|subscribed)=/.test(window.location.search));
  const [waiting, setWaiting] = useState(returning.current);

  const load = useCallback(async () => {
    try {
      const d = (await getBilling()).data;
      setData(d);
      setError(null);
      return d;
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not load billing.');
      return null;
    }
  }, []);

  // Back from HitPay: the payment is confirmed by HitPay's webhook, usually
  // within seconds, so check a few times before settling.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const first = await load();
      if (!returning.current) return;
      const before = first?.invoices.length ?? 0;
      for (let i = 0; i < 8 && !cancelled; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const d = await load();
        if (d && d.invoices.length > before) break;
      }
      if (!cancelled) {
        setWaiting(false);
        onChanged();
        window.history.replaceState(null, '', window.location.pathname);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load, onChanged]);

  async function go(key: string, fn: () => Promise<{ data: { url: string } }>) {
    setBusy(key);
    setError(null);
    try {
      const { url } = (await fn()).data;
      window.location.href = url;
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not open the payment page.');
      setBusy(null);
    }
  }

  if (!data) {
    return error ? (
      <p className="rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-200">{error}</p>
    ) : (
      <div className="flex justify-center py-12">
        <Spinner className="size-6" />
      </div>
    );
  }

  const { status, price, subscription: sub } = data;
  const cardRunning = sub?.method === 'card' && sub.status === 'active';
  const daysLeft = data.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(data.trialEndsAt).getTime() - Date.now()) / 86_400_000))
    : 0;

  return (
    <div className="max-w-2xl space-y-5">
      {waiting && (
        <p className="flex items-center gap-2 rounded-lg border border-sky-800 bg-sky-950 p-3 text-sm text-sky-200">
          <RefreshCw size={14} className="animate-spin" />
          Confirming your payment with HitPay…
        </p>
      )}
      {error && <p className="rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-200">{error}</p>}

      {/* Where the organisation stands */}
      <section className="rounded-xl border border-ink-700 bg-ink-800/40 p-5">
        <p className="field-label mb-1">Plan</p>
        {status === 'comped' && <p className="text-lg font-semibold text-emerald-300">Not charged</p>}
        {status === 'suspended' && <p className="text-lg font-semibold text-red-300">Suspended — contact BlisxBench</p>}
        {status === 'trial' && (
          <>
            <p className="text-lg font-semibold">
              Free trial — {daysLeft} day{daysLeft === 1 ? '' : 's'} left
            </p>
            <p className="mt-1 text-sm text-ink-400">
              Ends {day(data.trialEndsAt)}. Includes {data.trial.credits} AI credits and {mins(data.trial.speechSeconds)} of
              spoken notes. Subscribing now gives the full allowance straight away.
            </p>
          </>
        )}
        {status === 'paid' && (
          <>
            <p className="text-lg font-semibold">Lab plan</p>
            <p className="mt-1 text-sm text-ink-400">
              {cardRunning
                ? `Renews by card every ${sub!.cycle === 'yearly' ? 'year' : 'month'}. Next charge around ${day(data.paidThrough)}.`
                : `Paid to ${day(data.paidThrough)}.${sub?.status === 'cancelled' ? ' The card subscription is cancelled and will not renew.' : ' Pay again before then to keep full access.'}`}
            </p>
          </>
        )}
        {status === 'lapsed' && (
          <>
            <p className="text-lg font-semibold text-amber-300">Read-only</p>
            <p className="mt-1 text-sm text-ink-400">
              The {data.paidThrough ? 'subscription' : 'trial'} ended {day(data.paidThrough ?? data.trialEndsAt)}. Everything
              is still there to read and export. Subscribe to keep working.
            </p>
          </>
        )}
        <p className="mt-3 text-sm text-ink-400">
          Lab plan: {sgd(price.monthlySgd)} a month or {sgd(price.yearlySgd)} a year, up to {data.limits.members} members,{' '}
          {data.limits.creditsPerMonth} AI credits and {mins(data.limits.speechSecondsPerMonth)} of spoken notes a month.
        </p>
      </section>

      {/* Subscribe */}
      {status !== 'comped' && status !== 'suspended' && !cardRunning && (
        <section className="rounded-xl border border-ink-700 bg-ink-800/40 p-5">
          <p className="field-label mb-3">{status === 'paid' ? 'Renew or switch to card' : 'Subscribe'}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {(['monthly', 'yearly'] as Cycle[]).map((cycle) => (
              <div key={cycle} className="rounded-lg border border-ink-700 p-4">
                <p className="font-semibold">
                  {sgd(cycle === 'yearly' ? price.yearlySgd : price.monthlySgd)}{' '}
                  <span className="text-sm font-normal text-ink-400">/ {cycle === 'yearly' ? 'year' : 'month'}</span>
                </p>
                {cycle === 'yearly' && <p className="text-xs text-emerald-300">Two months free</p>}
                <div className="mt-3 space-y-2">
                  <Button
                    variant="primary"
                    className="w-full"
                    icon={<CreditCard size={15} />}
                    loading={busy === `card-${cycle}`}
                    disabled={!!busy}
                    onClick={() => go(`card-${cycle}`, () => start({ cycle, method: 'card' }))}
                  >
                    Card, renews automatically
                  </Button>
                  <Button
                    className="w-full"
                    icon={<QrCode size={15} />}
                    loading={busy === `paynow-${cycle}`}
                    disabled={!!busy}
                    onClick={() => go(`paynow-${cycle}`, () => start({ cycle, method: 'paynow' }))}
                  >
                    PayNow, one {cycle === 'yearly' ? 'year' : 'month'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-ink-400">
            Payments are handled by HitPay. Card subscriptions are charged today
            {status === 'paid' ? ' (or when the current period ends)' : ''} and then every period until cancelled. PayNow
            does not renew; pay again when the period ends. An invoice is emailed for every payment.
          </p>
        </section>
      )}

      {/* Running card subscription */}
      {cardRunning && (
        <section className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-ink-700 bg-ink-800/40 p-5">
          <div>
            <p className="font-medium">Card subscription, {sub!.cycle}</p>
            <p className="text-sm text-ink-400">Cancel any time; access continues to {day(data.paidThrough)}.</p>
          </div>
          {data.viewerRole === 'owner' && (
            <Button variant="ghost" onClick={() => setConfirmCancel(true)}>
              Cancel subscription
            </Button>
          )}
        </section>
      )}

      {/* Spoken notes add-on */}
      {status === 'paid' && (
        <section className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-ink-700 bg-ink-800/40 p-5">
          <div>
            <p className="font-medium">Spoken notes this month</p>
            <p className="text-sm text-ink-400">
              {mins(data.pool.speechSeconds)} used of {mins(data.pool.speechLimit)}. Add {mins(data.addOn.seconds)} for{' '}
              {sgd(data.addOn.priceSgd)}, this month only.
            </p>
          </div>
          <Button
            icon={<Mic size={15} />}
            loading={busy === 'addon'}
            disabled={!!busy}
            onClick={() => go('addon', () => addOn())}
          >
            Add {mins(data.addOn.seconds)}
          </Button>
        </section>
      )}

      {/* Invoices */}
      <section>
        <p className="field-label mb-2">Invoices</p>
        {data.invoices.length === 0 ? (
          <p className="text-sm text-ink-400">No payments yet.</p>
        ) : (
          <ul className="divide-y divide-ink-800 rounded-xl border border-ink-700">
            {data.invoices.map((inv) => (
              <li key={inv.id} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="truncate">{inv.items[0]?.description ?? inv.number}</p>
                  <p className="text-xs text-ink-400">
                    {inv.number} · {day(inv.paidAt)} · {inv.method}
                  </p>
                </div>
                <Badge tone="good">Paid</Badge>
                <span className="tabular-nums">{sgd(inv.total)}</span>
                <button
                  className={cx('icon-btn text-ink-400 hover:bg-ink-800 hover:text-ink-100')}
                  aria-label={`Download ${inv.number}`}
                  onClick={() => downloadInvoicePdf(inv)}
                >
                  <Download size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Modal open={confirmCancel} onClose={() => setConfirmCancel(false)} title="Cancel the subscription?">
        <p className="text-sm text-ink-300">
          No more charges. Everyone keeps full access until {day(data.paidThrough)}; after that the organisation becomes
          read-only until someone subscribes again.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setConfirmCancel(false)}>
            Keep it
          </Button>
          <Button
            variant="danger"
            loading={busy === 'cancel'}
            onClick={async () => {
              setBusy('cancel');
              try {
                await cancel();
                setConfirmCancel(false);
                await load();
              } catch (e) {
                setError((e as Error)?.message ?? 'Could not cancel.');
              } finally {
                setBusy(null);
              }
            }}
          >
            Cancel subscription
          </Button>
        </div>
      </Modal>
    </div>
  );
}
